import { useState } from 'react';
import { FirebaseBillingService } from '@/services/firebase-billing.service';
import { ExpenseService } from '@/services/expense.service';
import { Expense } from '@/models/expense.model';

export default function FirebaseBillingSync() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    expensesCreated: number;
    expenseIds: string[];
    errors: string[];
  } | null>(null);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [billingData, setBillingData] = useState({
    month: new Date().toISOString().slice(0, 7), // YYYY-MM format
    services: [
      { name: 'Cloud Firestore', amount: 0, skuId: '', location: 'us-central' },
      { name: 'Cloud Functions', amount: 0, skuId: '', location: 'us-central' },
      { name: 'Cloud Storage', amount: 0, skuId: '', location: 'us-central' },
    ],
    projectId: 'faircare-82071',
  });
  const [createVendor, setCreateVendor] = useState(true);

  const handleAddService = () => {
    setBillingData({
      ...billingData,
      services: [...billingData.services, { name: '', amount: 0, skuId: '', location: 'us-central' }],
    });
  };

  const handleRemoveService = (index: number) => {
    setBillingData({
      ...billingData,
      services: billingData.services.filter((_, i) => i !== index),
    });
  };

  const handleServiceChange = (index: number, field: string, value: string | number) => {
    const updated = [...billingData.services];
    updated[index] = { ...updated[index], [field]: value };
    setBillingData({ ...billingData, services: updated });
  };

  const handleSync = async () => {
    setLoading(true);
    setResult(null);
    setExpenses([]);

    try {
      const result = await FirebaseBillingService.syncBillingFromSimpleFormat({
        month: billingData.month,
        services: billingData.services.filter(s => s.name && s.amount > 0),
        projectId: billingData.projectId,
      });

      setResult(result);

      // Fetch created expenses
      if (result.expenseIds.length > 0) {
        const fetchedExpenses = await Promise.all(
          result.expenseIds.map(id => ExpenseService.getExpenseById(id))
        );
        setExpenses(fetchedExpenses.filter((e): e is Expense => e !== null));
      }
    } catch (error) {
      setResult({
        success: false,
        expensesCreated: 0,
        expenseIds: [],
        errors: [error instanceof Error ? error.message : 'Unknown error'],
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLoadExisting = async () => {
    setLoading(true);
    try {
      const allExpenses = await ExpenseService.getAllExpenses();
      const firebaseExpenses = allExpenses.filter(
        e => e.tags?.includes('firebase') || e.vendorName?.includes('Google')
      );
      setExpenses(firebaseExpenses);
    } catch (error) {
      console.error('Failed to load expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Firebase Billing Sync</h1>
        <p className="text-gray-600">
          Sync Firebase billing data to the expense collection. Expenses will be automatically
          created and tracked in the operations portal.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Billing Data</h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Billing Month
            </label>
            <input
              type="month"
              value={billingData.month}
              onChange={e => setBillingData({ ...billingData, month: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project ID
            </label>
            <input
              type="text"
              value={billingData.projectId}
              onChange={e => setBillingData({ ...billingData, projectId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              id="createVendor"
              checked={createVendor}
              onChange={e => setCreateVendor(e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="createVendor" className="text-sm text-gray-700">
              Create "Google Inc" vendor if it doesn't exist
            </label>
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">Services</label>
              <button
                onClick={handleAddService}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                + Add Service
              </button>
            </div>

            <div className="space-y-3">
              {billingData.services.map((service, index) => (
                <div key={index} className="grid grid-cols-12 gap-3 items-end">
                  <div className="col-span-4">
                    <input
                      type="text"
                      placeholder="Service name"
                      value={service.name}
                      onChange={e => handleServiceChange(index, 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="col-span-3">
                    <input
                      type="number"
                      placeholder="Amount"
                      value={service.amount || ''}
                      onChange={e => handleServiceChange(index, 'amount', parseFloat(e.target.value) || 0)}
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="col-span-3">
                    <input
                      type="text"
                      placeholder="SKU ID (optional)"
                      value={service.skuId}
                      onChange={e => handleServiceChange(index, 'skuId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="col-span-1">
                    <button
                      onClick={() => handleRemoveService(index)}
                      className="w-full px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={handleSync}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Syncing...' : 'Sync to Expenses'}
            </button>
            <button
              onClick={handleLoadExisting}
              disabled={loading}
              className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Load Existing Firebase Expenses
            </button>
          </div>
        </div>
      </div>

      {result && (
        <div className={`bg-white rounded-lg shadow-md p-6 mb-6 ${
          result.success ? 'border-l-4 border-green-500' : 'border-l-4 border-red-500'
        }`}>
          <h2 className="text-xl font-semibold mb-4">
            {result.success ? '✓ Sync Successful' : '✗ Sync Failed'}
          </h2>
          
          <div className="space-y-2">
            <p>
              <strong>Expenses Created:</strong> {result.expensesCreated}
            </p>
            
            {result.errors.length > 0 && (
              <div>
                <strong className="text-red-600">Errors:</strong>
                <ul className="list-disc list-inside mt-1 text-red-600">
                  {result.errors.map((error, i) => (
                    <li key={i}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {expenses.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">
            Firebase Expenses ({expenses.length})
          </h2>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vendor
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {expenses.map(expense => (
                  <tr key={expense.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {expense.expenseDate.toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {expense.subcategory || expense.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${expense.amount.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        expense.status === 'approved' || expense.status === 'paid'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {expense.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {expense.vendorName || 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
